import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWS } from '../contexts/WebSocketContext';
import api from '../services/api';
import {
  FiHome,
  FiUsers,
  FiMonitor,
  FiImage,
  FiKey,
  FiActivity,
  FiLogOut,
  FiSettings,
  FiUser,
  FiSend,
  FiList,
  FiHardDrive,
} from 'react-icons/fi';

const Sidebar = () => {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const [diskSpace, setDiskSpace] = useState(null);
  const { subscribe } = useWS();

  const isActive = (path) => location.pathname === path;

  // Fetch disk space on mount, then listen for WS updates
  useEffect(() => {
    const fetchDiskSpace = async () => {
      try {
        const endpoint = isAdmin ? '/admin/disk-space' : '/users/disk-space';
        const response = await api.get(endpoint);
        setDiskSpace(response.data);
      } catch (error) {
        console.error('Failed to fetch disk space:', error);
      }
    };

    fetchDiskSpace();
    // Subscribe to WS disk space updates instead of polling
    const unsub = subscribe('disk:space', (data) => setDiskSpace(data));
    // Fallback: poll every 5 minutes in case WS is down
    const interval = setInterval(fetchDiskSpace, 300000);
    return () => { unsub(); clearInterval(interval); };
  }, [isAdmin, subscribe]);

  // Format bytes to readable format
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const clientMenuItems = [
    // Overview Section
    { type: 'section', label: 'Overview' },
    { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    
    // Content Management Section
    { type: 'section', label: 'Content Management' },
    { path: '/content', icon: FiImage, label: 'Content Library' },
    { path: '/content-publishing', icon: FiSend, label: 'Publish Content' },
    { path: '/publishing-tasks', icon: FiList, label: 'Publishing Tasks' },
    
    // Device Management Section
    { type: 'section', label: 'Device Management' },
    { path: '/devices', icon: FiMonitor, label: 'Device Configuration' },
    { path: '/device-media', icon: FiActivity, label: 'Device Media' },
    
    // System Section
    { type: 'section', label: 'System' },
    { path: '/licenses', icon: FiKey, label: 'Licenses' },
    { path: '/account-settings', icon: FiSettings, label: 'Account Settings' },
  ];

  const adminMenuItems = [
    // Overview Section
    { type: 'section', label: 'Overview' },
    { path: '/admin/dashboard', icon: FiHome, label: 'Dashboard' },
    
    // Client Management Section
    { type: 'section', label: 'Client Management' },
    { path: '/admin/companies', icon: FiUsers, label: 'Companies' },
    { path: '/admin/users', icon: FiUser, label: 'Users' },
    
    // System Management Section
    { type: 'section', label: 'System Management' },
    { path: '/admin/licenses', icon: FiKey, label: 'Licenses' },
    { path: '/admin/settings', icon: FiSettings, label: 'System Settings' },
  ];

  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  return (
    <div className="bg-gray-900 text-white w-64 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-gray-700">
        <h1 className="text-2xl font-bold text-white">AdvDigit</h1>
        <p className="text-gray-400 text-sm mt-1">Digital Signage</p>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
            <span className="text-white font-medium">
              {isAdmin ? 'A' : user?.firstName?.[0] || 'U'}
            </span>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium">
              {isAdmin ? 'Administrator' : `${user?.firstName} ${user?.lastName}`}
            </p>
            <p className="text-xs text-gray-400">
              {isAdmin ? 'System Admin' : user?.compName}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6">
        <ul className="space-y-1">
          {menuItems.map((item, index) => {
            if (item.type === 'section') {
              return (
                <li key={`section-${index}`} className="pt-4 first:pt-0">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-4">
                    {item.label}
                  </h3>
                </li>
              );
            }

            const Icon = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-3" />
                  <span className="text-sm">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Disk Space */}
      {diskSpace && (
        <div className="p-4 border-t border-gray-700">
          <div className="px-4 py-3 bg-gray-800 rounded-lg">
            <div className="flex items-center mb-2">
              <FiHardDrive className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-xs font-semibold text-gray-400 uppercase">Disk Space</span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-300">
                <span>Used: {formatBytes(diskSpace.used)}</span>
                <span>Total: {formatBytes(diskSpace.total)}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    diskSpace.percentage > 90
                      ? 'bg-red-500'
                      : diskSpace.percentage > 70
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${diskSpace.percentage}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-400 text-center">
                {diskSpace.percentage}% used
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout */}
      <div className="p-4 border-t border-gray-700">
        <button
          onClick={logout}
          className="flex items-center w-full px-4 py-3 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors"
        >
          <FiLogOut className="w-5 h-5 mr-3" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
